import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import { ModalShell, ModalHeader } from '../../../components/ui/ModalFrame';
import Input from '../../../components/ui/Input';
import licenseService from '../../../services/licenseService';
import userService from '../../../services/userService';
import { useMercadoPagoSdk } from '../../../hooks/useMercadoPagoSdk';
import { MODAL_LAYER } from '../../../constants/modalLayers';
import { MODAL_FOOTER_PAYMENT_ACTIONS } from '../../../constants/layoutSpacing';
import { formatLicensePriceOrConsult } from '../../../utils/formatCurrencyBRL';
import { cpfDigitsOnly, isValidCpf, maskCpf, maskPersonName } from 'utils/masks';
import SubscriptionCardCheckoutPanel from './SubscriptionCardCheckoutPanel';
import SubscriptionResultPanel from './SubscriptionResultPanel';

const SubscriptionPaymentModal = ({ license, onClose, changePlan = false }) => {
  const { sdk, ready, loading: sdkLoading, error: sdkError } = useMercadoPagoSdk();
  const [customerName, setCustomerName] = useState('');
  const [customerCpf, setCustomerCpf] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [subscriptionResult, setSubscriptionResult] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAccountEmail = async () => {
      const result = await userService.getUserInfo();
      if (!cancelled && result?.success && result.data?.email) {
        setAccountEmail(result.data.email);
      }
    };

    loadAccountEmail();
    return () => {
      cancelled = true;
    };
  }, []);

  const isFormValid = customerName.trim().length > 0 && isValidCpf(customerCpf);

  return (
    <ModalShell
      backdropClassName="bg-background/80"
      zClass={MODAL_LAYER.payment}
      panelClassName="w-full max-w-2xl h-[95vh] bg-card border rounded-lg flex flex-col shadow-elevation-lg"
      backdropPadding="none"
    >
      <ModalHeader
        title={changePlan ? 'Trocar plano da assinatura' : 'Assinar mensalmente'}
        onClose={onClose}
        titleClassName="text-xl font-semibold"
      />

      <div className="px-6 pt-2 space-y-4 overflow-y-auto flex-1">
        <div className="rounded-lg border border-border bg-muted/20 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Plano</p>
            <p className="font-semibold text-foreground">{license?.license_name}</p>
          </div>
          <p className="text-lg font-heading font-bold text-foreground">
            {formatLicensePriceOrConsult(license?.price)}/mês
          </p>
        </div>

        {!subscriptionResult && (
          <div className="space-y-4">
            {accountEmail && (
              <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                <p className="text-xs text-muted-foreground">E-mail da sua conta</p>
                <p className="text-sm text-foreground break-all">{accountEmail}</p>
              </div>
            )}
            <Input
              label="Nome completo"
              value={customerName}
              onChange={e => setCustomerName(maskPersonName(e.target.value))}
              required
            />
            <Input
              label="CPF"
              inputMode="numeric"
              autoComplete="off"
              value={maskCpf(customerCpf)}
              onChange={e => setCustomerCpf(cpfDigitsOnly(e.target.value))}
              required
            />
          </div>
        )}

        {sdkLoading && (
          <p className="text-xs text-muted-foreground">Carregando formulário seguro do Mercado Pago...</p>
        )}

        {sdkError && (
          <div className="p-3 text-destructive border rounded text-sm">
            Não foi possível carregar o checkout de cartão. Tente novamente em instantes.
          </div>
        )}

        {errorMessage && (
          <div className="p-3 text-destructive border rounded text-sm">{errorMessage}</div>
        )}

        {subscriptionResult ? (
          <SubscriptionResultPanel result={subscriptionResult} onClose={onClose} />
        ) : ready && isFormValid && accountEmail ? (
          <SubscriptionCardCheckoutPanel
            sdk={sdk}
            amount={license?.price}
            license={license}
            accountEmail={accountEmail}
            customerName={customerName}
            customerCpf={customerCpf}
            onSubscriptionResult={setSubscriptionResult}
            onError={setErrorMessage}
            changePlan={changePlan}
          />
        ) : null}
      </div>

      {!subscriptionResult && (
        <div className={MODAL_FOOTER_PAYMENT_ACTIONS}>
          <Button variant="outline" fullWidth onClick={onClose}>
            Voltar
          </Button>
          {!isFormValid && (
            <p className="text-xs text-muted-foreground text-center flex-1">
              Informe nome completo e CPF válido para habilitar o cartão
            </p>
          )}
        </div>
      )}
    </ModalShell>
  );
};

export default SubscriptionPaymentModal;
